import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, Sparkles, ArrowRight } from 'lucide-react';
import SocialEngagementBlock from './SocialEngagementBlock';

// Free monthly "Win a Sim Racing Experience" entry form. Collects the enhanced
// customer details (DOB, full postal address, social handles + engagement).
export default function FreeEntryForm({ currentMonth, currentYear }) {
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', postcode: '',
    date_of_birth: '', address_line1: '', address_city: '',
    experience_preference: 'any', marketing_opt_in: false,
    liked_post: false, shared_post: false, followed_social: false,
    instagram_handle: '', facebook_handle: '', tiktok_handle: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      setError('Please enter your first name, last name and email.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!form.phone.trim() || !form.address_line1.trim() || !form.address_city.trim() || !form.postcode.trim()) {
      setError('Please enter your phone number and full postal address — we need these to contact you if you win.');
      return;
    }
    if (!form.marketing_opt_in) {
      setError('Please tick the mailing list box to enter — we need to be able to contact you if you win.');
      return;
    }
    if (form.date_of_birth) {
      const age = (Date.now() - new Date(form.date_of_birth).getTime()) / (365.25 * 24 * 3600 * 1000);
      if (age < 5) { setError('You must be at least 5 years old to enter.'); return; }
    }
    setSubmitting(true);
    try {
      await base44.entities.CompetitionEntry.create({
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        postcode: form.postcode.trim(),
        date_of_birth: form.date_of_birth || '',
        address_line1: form.address_line1.trim(),
        address_city: form.address_city.trim(),
        experience_preference: form.experience_preference,
        marketing_opt_in: form.marketing_opt_in,
        liked_post: form.liked_post,
        shared_post: form.shared_post,
        followed_social: form.followed_social,
        instagram_handle: form.instagram_handle.trim(),
        facebook_handle: form.facebook_handle.trim(),
        tiktok_handle: form.tiktok_handle.trim(),
        competition_type: 'monthly_free',
        source: 'website',
      });
      // Auto-subscribe free entrants to the mailing list so we can contact winners.
      try {
        await base44.functions.invoke('subscribeToNewsletter', {
          email: form.email.trim().toLowerCase(),
          source: 'competition_free',
        });
      } catch (_) { /* non-blocking — entry still counts */ }
      setDone(true);
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="border border-green-500/30 bg-green-500/5 p-8 text-center">
        <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-4" />
        <h2 className="text-white font-black text-2xl uppercase mb-2">You're in!</h2>
        <p className="text-white/50 text-sm max-w-md mx-auto">
          Your free entry for {currentMonth} {currentYear} has been recorded. The first winner is drawn on
          30 September 2026, then monthly on the 1st — keep an eye on your inbox. Good luck!
        </p>
        <button
          onClick={() => {
            setDone(false);
            setForm({
              first_name: '', last_name: '', email: '', phone: '', postcode: '',
              date_of_birth: '', address_line1: '', address_city: '',
              experience_preference: 'any', marketing_opt_in: false,
              liked_post: false, shared_post: false, followed_social: false,
              instagram_handle: '', facebook_handle: '', tiktok_handle: '',
            });
          }}
          className="mt-6 text-primary hover:text-primary/80 text-xs font-bold tracking-widest underline"
        >
          ENTER AGAIN NEXT MONTH
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="border border-white/10 bg-[#111115] p-6 sm:p-8">
      <div className="flex items-center gap-2 mb-6">
        <Sparkles className="w-4 h-4 text-primary" />
        <h2 className="text-white font-bold text-lg">Enter the free monthly draw</h2>
      </div>

      {error && <div className="mb-4 px-4 py-3 bg-red-500/5 border border-red-500/30 text-red-300 text-xs">{error}</div>}

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="FIRST NAME *"><input name="first_name" value={form.first_name} onChange={handleChange} className="seo-input" autoComplete="given-name" /></Field>
        <Field label="LAST NAME *"><input name="last_name" value={form.last_name} onChange={handleChange} className="seo-input" autoComplete="family-name" /></Field>
        <Field label="EMAIL *" full><input name="email" type="email" value={form.email} onChange={handleChange} className="seo-input" autoComplete="email" /></Field>
        <Field label="DATE OF BIRTH"><input name="date_of_birth" type="date" value={form.date_of_birth} onChange={handleChange} className="seo-input" /></Field>
        <Field label="PHONE *"><input name="phone" value={form.phone} onChange={handleChange} className="seo-input" autoComplete="tel" /></Field>
        <Field label="ADDRESS LINE 1 *"><input name="address_line1" value={form.address_line1} onChange={handleChange} className="seo-input" autoComplete="address-line1" /></Field>
        <Field label="CITY / TOWN *"><input name="address_city" value={form.address_city} onChange={handleChange} className="seo-input" autoComplete="address-level2" /></Field>
        <Field label="POSTCODE *"><input name="postcode" value={form.postcode} onChange={handleChange} className="seo-input" autoComplete="postal-code" /></Field>
        <Field label="EXPERIENCE YOU'D LIKE">
          <select name="experience_preference" value={form.experience_preference} onChange={handleChange} className="seo-input">
            <option value="any">Any experience</option>
            <option value="adult">Adult (16+)</option>
            <option value="child">Child (8–15)</option>
            <option value="young_racer">Young Racer (5–7)</option>
          </select>
        </Field>
      </div>

      <SocialEngagementBlock form={form} setForm={setForm} />

      <label className="flex items-start gap-3 mt-5 cursor-pointer">
        <input type="checkbox" name="marketing_opt_in" checked={form.marketing_opt_in} onChange={handleChange} className="mt-1 accent-[#e11d2a]" />
        <span className="text-white/40 text-xs leading-relaxed">
          Add me to the Motion Reality mailing list for news, offers and competition reminders. You can unsubscribe at any time. <span className="text-white/60">Required to enter</span> — we'll use these details to contact you if you win.
        </span>
      </label>

      <p className="text-white/30 text-[11px] leading-relaxed mt-5">
        By entering you agree to the competition <Link to="/terms-conditions" className="text-primary/70 hover:text-primary underline">terms</Link>. One entry per person per month. Open to UK residents aged 5+. Winner drawn at random and contacted by email. No purchase necessary.
      </p>

      <button
        type="submit"
        disabled={submitting}
        className="mt-6 w-full bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-6 py-4 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
      >
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> SUBMITTING…</> : <>ENTER THE DRAW <ArrowRight className="w-4 h-4" /></>}
      </button>
    </form>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}